import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '../../api/auth'
import type { SessionDto } from '../../api/types'

export function SessionsSection() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const sessions = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => authApi.listSessions(),
  })
  const [error, setError] = useState<string | null>(null)

  const revoke = useMutation({
    mutationFn: (id: string) => authApi.revokeSession(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'sessions'] }),
    onError: (e) => setError(String(e)),
  })

  const revokeAll = useMutation({
    mutationFn: () => authApi.revokeAllOtherSessions(),
    // Сервер заодно снимает доверие с других устройств (6.1)
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth', 'sessions'] })
      void qc.invalidateQueries({ queryKey: ['auth', 'trusted-devices'] })
    },
    onError: (e) => setError(String(e)),
  })

  if (sessions.isLoading) return <div className="text-sm text-gray-500">{t('common.loading')}</div>
  const list = sessions.data ?? []
  const otherCount = list.filter((s) => !s.isCurrent).length

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        {t('sessionsSection.intro')}
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {list.map((s) => (
          <SessionRow
            key={s.id}
            session={s}
            disabled={revoke.isPending}
            onRevoke={() => revoke.mutate(s.id)}
            t={t}
          />
        ))}
      </div>

      {otherCount > 0 && (
        <button
          type="button"
          onClick={() => revokeAll.mutate()}
          disabled={revokeAll.isPending}
          className="text-sm text-red-600 hover:underline"
        >
          {revokeAll.isPending
            ? t('sessionsSection.revokingAll')
            : t('sessionsSection.revokeAllOthers', { n: otherCount })}
        </button>
      )}
    </div>
  )
}

function SessionRow({
  session,
  disabled,
  onRevoke,
  t,
}: {
  session: SessionDto
  disabled: boolean
  onRevoke: () => void
  t: TFunction
}) {
  const browser = describeUserAgent(session.userAgent ?? '', t)
  return (
    <div className="flex items-start justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>{browser}</span>
          {session.isCurrent && (
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
              {t('sessionsSection.current')}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {session.ipAddr ?? '—'} · {t('sessionsSection.loggedIn')} {formatRelative(session.createdAt, t)} ·{' '}
          {t('sessionsSection.activity')} {formatRelative(session.lastSeenAt, t)}
        </div>
      </div>
      {!session.isCurrent && (
        <button
          type="button"
          onClick={onRevoke}
          disabled={disabled}
          className="text-xs text-red-600 hover:underline shrink-0"
        >
          {t('sessionsSection.revoke')}
        </button>
      )}
    </div>
  )
}

/** Короткое описание устройства по User-Agent (без полноценного парсера). */
export function describeUserAgent(ua: string, t: TFunction): string {
  if (!ua) return t('sessionsSection.unknownDevice')
  // упрощённый детект — без полноценного парсера
  const isMobile = /Mobile|Android|iPhone|iPad/.test(ua)
  let browser = t('sessionsSection.browser')
  if (/Edg/.test(ua)) browser = 'Edge'
  else if (/Chrome/.test(ua)) browser = 'Chrome'
  else if (/Safari/.test(ua)) browser = 'Safari'
  else if (/Firefox/.test(ua)) browser = 'Firefox'
  let os = ''
  if (/Windows/.test(ua)) os = 'Windows'
  else if (/Mac OS|Macintosh/.test(ua)) os = 'macOS'
  else if (/Linux/.test(ua)) os = 'Linux'
  else if (/Android/.test(ua)) os = 'Android'
  else if (/iPhone|iPad/.test(ua)) os = 'iOS'
  return `${browser}${os ? ' · ' + os : ''}${isMobile ? ' ' + t('sessionsSection.mobile') : ''}`
}

function formatRelative(ts: number, t: TFunction): string {
  const diff = Date.now() - ts
  const min = Math.floor(diff / 60_000)
  if (min < 1) return t('sessionsSection.justNow')
  if (min < 60) return t('sessionsSection.minAgo', { n: min })
  const hours = Math.floor(min / 60)
  if (hours < 24) return t('sessionsSection.hoursAgo', { n: hours })
  const days = Math.floor(hours / 24)
  if (days < 7) return t('sessionsSection.daysAgo', { n: days })
  return new Date(ts).toLocaleDateString('ru')
}
